import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import SuggestedProjectPickerComponent from "../suggestedProjectPicker/SuggestedProjectPickerComponent";
import { ITag } from "office-ui-fabric-react";
import { NewformaToastIntent } from "../../_base/Toast";
import { Logger } from "../../../services/Logger";
import { MailboxItem } from "../../../services/OfficeWrapper";
import { SmartFilingManager } from "../../../services/SmartFiling/SmartFilingManager";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { InvalidateCacheService } from "../../../services/NewformaApi/InvalidateCacheService";
import { MessageTypes, Messenger } from "../../../services/Messenger";
import { OfficeRoamingSettings } from "../../../services/OfficeRoamingSettings";
import { ITagWithNixEnabled } from "../../../models/shared/ITagWithNixEnabed";
import { Project } from "../../../models/ProjectsResponse";
import { Paging } from "../../../models/ProjectKeywordsResponse";
import { ExpiredSessionError } from "../../../models/ExpiredSessionError";
import { ProjectsCacheKeys } from "../../../models/StorageKeys";

interface ProjectPickerComponentProps extends LocalizeContextProps {
    allowGlobalProjects: boolean;
    logger: Logger;
    onExpiredSession: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    mailboxItem: MailboxItem | null;
    smartFilingManager: SmartFilingManager;
    projectsService: IProjectsService;
    invalidateCacheService: InvalidateCacheService;
    shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache: boolean;
    theme: string;
    messenger: Messenger;
    officeRoamingSettings: OfficeRoamingSettings;
    culture: string;
    showNoProjectsToast: () => void;
    hideNoProjectsToast: () => void;
    onProjectSelected: (project: ITagWithNixEnabled | null) => void;
    isFiling: boolean;
    onLoadingChange: (key: string, isLoading: boolean) => void;
    updateProjects: (projects: ExtendedITag<string>[]) => void;
    allowProjectDisabled?: boolean;
    shouldParseEmailSubject?: boolean;
}
interface ProjectPickerComponentState {
    isLoadingProjects: boolean;
    isLoadingGlobalProjects: boolean;
    projects: ExtendedITag<string>[];
    myProjects: ExtendedITag<string>[];
    selectedProject: ITag | null;
    searchTerm: string | null;
    shouldUpdateProjects: boolean;
    isLoadingRemainingProjects: boolean;
}

interface ExtendedITag<T> extends ITag {
    additionalValue: T;
}

// The goal of this component is to consolidate project loading
// so that it can eventually be removed from every page and exist in one place

class ProjectPickerComponent extends React.Component<ProjectPickerComponentProps, ProjectPickerComponentState> {
    componentName: string = "ProjectLoadingWrapperComponent";

    constructor(props: ProjectPickerComponentProps, context: ProjectPickerComponentState) {
        super(props, context);
        this.state = {
            isLoadingProjects: false,
            isLoadingGlobalProjects: false,
            projects: [],
            myProjects: [],
            selectedProject: null,
            searchTerm: null,
            shouldUpdateProjects: false,
            isLoadingRemainingProjects: false,
        };
    }

    async componentDidMount(): Promise<void> {
        this.props.logger.info(`${this.componentName} mounted`);
        await this.refreshProjects();
    }

    private async loadMyProjects(): Promise<Paging> {
        this.props.onLoadingChange(this.componentName, true);
        this.setState({ isLoadingProjects: true, isLoadingGlobalProjects: false });
        const globalProjectsOffsetToken = "globalProject";
        try {
            this.props.logger.info(`${this.componentName} - loading My projects.`);
            const startTime = new Date();

            const projectsResponse = await this.props.projectsService.getProjectsSupportingProjectItems();
            const projectsITag: ExtendedITag<string>[] = this.mapProjects(projectsResponse.projects);

            this.setState({ projects: projectsITag, myProjects: projectsITag });
            this.props.updateProjects(this.state.projects);
            const stopTime = new Date();
            const diffTime = (stopTime.getTime() - startTime.getTime()) / 1000;
            this.props.logger.info(
                `${this.componentName} - My projects loaded successfully in ${diffTime.toString()} sec`
            );
            return {
                offsetToken: projectsResponse.paging?.offsetToken || globalProjectsOffsetToken,
            };
        } catch (error) {
            this.props.logger.error(`${this.componentName} - error loading My projects`);
            this.handleApiError(error, this.props.translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string);
            this.setState({
                projects: [],
                myProjects: [],
            });
            this.props.updateProjects([]);
            return {
                offsetToken: globalProjectsOffsetToken,
            };
        } finally {
            this.setState({ isLoadingProjects: false });
            this.props.onLoadingChange(this.componentName, false);
        }
    }

    private async loadGlobalProjects(paging: Paging): Promise<void> {
        if (!this.props.allowGlobalProjects || !this.props.officeRoamingSettings.getShowAllGlobalProjects()) {
            return;
        }
        this.setState({ isLoadingGlobalProjects: true });
        this.props.logger.info(`${this.componentName} - Start loading global projects.  `);
        try {
            let updatedPaging: Paging | undefined = paging;
            const startTime = new Date();
            let pageCount = 0;
            do {
                const startTimeInner = new Date();
                pageCount++;
                const globalProjects: {
                    projects: ExtendedITag<string>[];
                    paging?: Paging;
                } = await this.getGlobalProjects(updatedPaging);

                const diffTimeInner = (Date.now() - startTimeInner.getTime()) / 1000;
                const newProjectsState = this.state.projects.concat(globalProjects.projects);

                const upTotal = newProjectsState.length.toString();
                this.setState({ projects: newProjectsState });
                updatedPaging = globalProjects.paging;
                this.props.logger.info(
                    `${
                        this.componentName
                    } - Page ${pageCount} [ ${upTotal.toString()} ] loaded successfully ${diffTimeInner.toString()} sec. Next OffsetToken ${
                        updatedPaging?.offsetToken
                    }`
                );
            } while (updatedPaging?.offsetToken);

            const diffTime = (Date.now() - startTime.getTime()) / 1000;

            this.props.logger.info(
                `${this.componentName} - Loading all projects completed in ${diffTime.toString()} sec`
            );
        } catch (error) {
            this.props.logger.error(`${this.componentName} - error loading all projects`, error);
            this.handleApiError(error, this.props.translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string);
        } finally {
            this.setState({ isLoadingGlobalProjects: false });
        }
    }

    private async getGlobalProjects(paging: Paging): Promise<{ projects: ExtendedITag<string>[]; paging?: Paging }> {
        const projectsResponse = await this.props.projectsService.getGlobalProjects(paging);
        const projectsITag: ExtendedITag<string>[] = this.mapProjects(projectsResponse.projects);
        return {
            projects: projectsITag,
            paging: projectsResponse.paging,
        };
    }

    private handleApiError(error: any, messageToDisplay: string): void {
        this.props.logger.error(`FileMultipleEmailComponent API error: ${messageToDisplay}`, error);

        if (ExpiredSessionError.isInstanceOf(error)) {
            this.props.onExpiredSession();
            return;
        }
        this.props.onShowToast(messageToDisplay, "error");
    }

    private async refreshProjects(): Promise<void> {
        if (!this.state.isLoadingGlobalProjects && !this.state.isLoadingProjects) {
            this.props.onProjectSelected(null);
            this.props.logger.info(`${this.componentName} Refreshing Cache`, "");
            await this.props.invalidateCacheService.invalidateCache(ProjectsCacheKeys.projectItemsCacheName);

            const paging = await this.loadMyProjects();
            this.props.updateProjects(this.state.projects);
            await this.loadGlobalProjects(paging);
        } else {
            this.props.logger.info(
                `${this.componentName} RefreshProjects ignored because isLoadingProjects is true`,
                ""
            );
        }
        if (this.state.myProjects?.length === 0) {
            this.props.showNoProjectsToast();
            this.props.onShowToast(this.props.translate("SHARED.ERRORS.NO_PROJECTS") as string, "error");
        } else {
            this.props.hideNoProjectsToast();
        }
    }

    private mapProjects(projects: Project[]): ExtendedITag<string>[] {
        return projects.map((project: any) => {
            const projectDisplay = project.number ? `${project.number} - ${project.name}` : project.name;
            return {
                key: project.nrn,
                name: projectDisplay,
                code: project.code,
                additionalValue: project.autoFileMailboxFolderName,
                isDisable: !(project.rfiEnabled || project.submittalEnabled || project.actionitemEnabled),
                number: project.number,
            };
        });
    }

    render() {
        return (
            <SuggestedProjectPickerComponent
                logger={this.props.logger}
                className="newforma-formSpacing"
                projects={this.state.projects}
                onProjectSelected={this.props.onProjectSelected.bind(this)}
                disabled={this.state.isLoadingProjects || this.props.isFiling}
                smartFilingManager={this.props.smartFilingManager}
                isLoadingProjects={this.state.isLoadingProjects}
                mailboxItem={this.props.mailboxItem}
                onRefresh={this.refreshProjects.bind(this)}
                theme={this.props.theme}
                isLoadingRemainingProjects={this.state.isLoadingGlobalProjects}
                myProjects={this.state.myProjects}
                allowProjectDisabled={this.props.allowProjectDisabled}
                shouldParseEmailSubject={this.props.shouldParseEmailSubject}
            />
        );
    }
}

export default withLocalize(ProjectPickerComponent);
